/**
 * Mobile detection utility for frontend web app
 * Detects if the user is on a mobile device and should be redirected to the mobile app
 */

/**
 * Check if the user is on a mobile device
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  // Check user agent
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Mobile device patterns
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(userAgent);

  // Check screen size (fallback)
  const isMobileScreen = window.innerWidth <= 768;

  // Check for touch support
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return isMobileUA || (isMobileScreen && isTouchDevice);
};

/**
 * Check if the user is on iOS
 * @returns {boolean}
 */
export const isIOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
};

/**
 * Check if the user is on Android
 * @returns {boolean}
 */
export const isAndroid = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android/i.test(userAgent);
};

/**
 * Get the platform name
 * @returns {string} 'ios' | 'android' | 'desktop'
 */
export const getPlatform = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
};

/**
 * Check if the user has already dismissed the mobile app banner
 * @returns {boolean}
 */
export const hasDismissedMobileBanner = () => {
  try {
    const dismissed = localStorage.getItem('lodgeic_mobile_banner_dismissed');
    const dismissedAt = localStorage.getItem('lodgeic_mobile_banner_dismissed_at');

    if (!dismissed || !dismissedAt) return false;

    // Check if dismissed within the last 7 days
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
    const dismissedTime = new Date(dismissedAt).getTime();
    const currentTime = new Date().getTime();

    return (currentTime - dismissedTime) < sevenDaysInMs;
  } catch (error) {
    console.error('Error checking mobile banner dismissal:', error);
    return false;
  }
};

/**
 * Mark the mobile app banner as dismissed
 */
export const dismissMobileBanner = () => {
  try {
    localStorage.setItem('lodgeic_mobile_banner_dismissed', 'true');
    localStorage.setItem('lodgeic_mobile_banner_dismissed_at', new Date().toISOString());
  } catch (error) {
    console.error('Error dismissing mobile banner:', error);
  }
};

/**
 * Check if we should show the mobile redirect
 * @returns {boolean}
 */
export const shouldShowMobileRedirect = () => {
  return isMobileDevice() && !hasDismissedMobileBanner();
};

/**
 * Get the app store URL based on platform
 * @returns {string}
 */
export const getAppStoreUrl = () => {
  const platform = getPlatform();

  // TODO: Replace these with your actual app store URLs when published
  const APP_STORE_URLS = {
    ios: 'https://apps.apple.com/us/app/lodgeic/id6752351703', // Replace with your iOS app ID
    android: 'https://play.google.com/store/apps/details?id=com.lodgeic.app', // Replace with your Android package name
  };

  return APP_STORE_URLS[platform] || null;
};

/**
 * Open the mobile app or redirect to app store
 * Uses deep linking to try to open the app first, then falls back to app store
 * @param {string} path - Optional path to deep link to (e.g., '/dashboard')
 */
export const openMobileApp = (path = '') => {
  const appScheme = 'lodgeic://'; // Your app's custom URL scheme
  const deepLink = `${appScheme}${path}`;
  const appStoreUrl = getAppStoreUrl();

  // Track if the page is hidden (app opened successfully)
  let appOpened = false;
  const startTime = Date.now();

  // Set up visibility change listener
  const visibilityChangeHandler = () => {
    if (document.hidden) {
      appOpened = true;
    }
  };

  // Set up blur listener (backup for when page loses focus)
  const blurHandler = () => {
    appOpened = true;
  };

  document.addEventListener('visibilitychange', visibilityChangeHandler);
  window.addEventListener('blur', blurHandler);
  window.addEventListener('pagehide', blurHandler);

  // Try to open the app with deep link
  window.location.href = deepLink;

  // Check after a delay if the app opened
  setTimeout(() => {
    // Clean up listeners
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    window.removeEventListener('blur', blurHandler);
    window.removeEventListener('pagehide', blurHandler);

    // If page wasn't hidden and not much time passed, app probably didn't open
    const timePassed = Date.now() - startTime;

    // Only redirect to store if:
    // 1. App didn't open (page wasn't hidden)
    // 2. Not too much time passed (user might have been in app)
    if (!appOpened && !document.hidden && timePassed < 3000) {
      if (appStoreUrl) {
        window.location.href = appStoreUrl;
      }
    }
  }, 2500);
};

export default {
  isMobileDevice,
  isIOS,
  isAndroid,
  getPlatform,
  hasDismissedMobileBanner,
  dismissMobileBanner,
  shouldShowMobileRedirect,
  getAppStoreUrl,
  openMobileApp,
};
